import React from 'react';
import { useAccount } from '@azure/msal-react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as MuiLink, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { useGetApmQuery } from '../../services/appSlice/appSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSepQuery } from '../../services/sepSlice';
import { useGetDataFieldsBySEPIDQuery } from '../../services/dataFieldsSlice';
import { spacingTokens } from '../../theme/spacingTokens';
import { typographyTokens, typography } from '../../theme/typographyTokens';
import PopOver from '../../components/PopOver/PopOver';
import APMFields from '../../components/APMFields';
import { APM } from '../../../shared/types/APM';
import { env } from '../../../shared/env';

interface Props {
  sepID?: number;
  sep?: any;
  dataFields?: any[];
}

const TOP_ROW_FIELDS: { dbName: string; label: string; always: boolean; labelMaxWidth?: number }[] = [
  { dbName: '__requestDate__', label: 'Request Date', always: true, labelMaxWidth: 100 },
  { dbName: '__validationDate__', label: 'Validated Date', always: true, labelMaxWidth: 100 },
  { dbName: '__apmid__', label: 'APMID', always: true },
  { dbName: 'Business Unit', label: 'Business Unit', always: true, labelMaxWidth: 100 },
  { dbName: 'Request Area', label: 'Request Area', always: true, labelMaxWidth: 100 },
  { dbName: 'Service Type', label: 'Service Type', always: true, labelMaxWidth: 100 },
  { dbName: 'Is this an existing application or a new application?', label: 'New or Existing App', always: true, labelMaxWidth: 100 },
  { dbName: 'Does the request include use of Artificial Intelligence (AI) or Machine Learning (ML)?', label: 'Use of AI or ML', always: true, labelMaxWidth: 100 },
  { dbName: 'Is Nuclear in scope for this request?', label: 'Nuclear in Scope', always: true, labelMaxWidth: 100 },
];

const TOP_ROW_DB_NAMES = new Set(TOP_ROW_FIELDS.map((f) => f.dbName));

const formatDate = (raw: string | Date | null | undefined): string => {
  if (!raw) return '';
  const d = new Date(raw as string);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const getDataFieldValue = (field: any): string => {
  if (!field) return '';
  if (field.value !== undefined && field.value !== null && field.value !== '') return String(field.value);
  if (Array.isArray(field.dataFieldOptions)) {
    const selected = field.dataFieldOptions.filter((o: any) => o.selected).map((o: any) => o.value);
    if (selected.length > 0) return selected.join(', ');
  }
  return '';
};

const formatDisplayValue = (value: string): string => {
  if (!value || value === '') return value;
  // Check if the string contains any digits
  const hasNumbers = /\d/.test(value);
  if (!hasNumbers && value.length > 0) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
};

// ── Style tokens (non-color properties only — colors are theme-aware) ────────
const LABEL_BASE_SX = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: typography.caption.fontSize,
  fontStyle: 'normal' as const,
  fontWeight: typographyTokens.fontWeights.medium,
  lineHeight: '16px',
  letterSpacing: '0.46px',
  textTransform: 'uppercase' as const,
  whiteSpace: 'normal' as const,
  wordBreak: 'normal' as const,
};

const VALUE_BASE_SX = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: typography.body2.fontSize,
  fontStyle: 'normal' as const,
  fontWeight: typographyTokens.fontWeights.regular,
  lineHeight: '125%',
  letterSpacing: typography.subtitle1.letterSpacing,
  fontFeatureSettings: "'liga' off, 'clig' off",
  whiteSpace: 'normal' as const,
  overflowWrap: 'break-word' as const,
};

interface MetaItem {
  label: string;
  value: string;
  labelMaxWidth?: number;
  popover?: React.ReactNode;
  extraContent?: React.ReactNode;
}

// ── MetadataItem ─────────────────────────────────────────────────────────────
// Renders as 3 grid cells (label / divider / value) placed into explicit rows
// of the parent row-grid so ALL labels share row 1, dividers share row 2,
// values share row 3 → perfect alignment within every row.
const MetadataItem = ({ item, colIndex }: { item: MetaItem; colIndex: number }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Label: brand teal — readable on both light and dark backgrounds
  const labelColor = theme.palette.prime.blue; // 
  // Value: dark navy in light mode, soft white in dark mode
  const valueColor = isDark ? theme.palette.text.primary : theme.palette.content.default;
  // Divider: theme-aware subtle line
  const dividerColor = theme.palette.divider;

  return (
    <Box sx={{my: spacingTokens.md}}>
      {/* Row 1: label — min 40px, bottom-aligned */}
      <Box
        sx={{
          gridRow: 1,
          gridColumn: colIndex,
          display: 'flex',
          minHeight: '40px',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          flexShrink: 0,
          alignSelf: 'stretch',
        }}
      >
        <Typography
          component="div"
          sx={{ ...LABEL_BASE_SX, color: labelColor, maxWidth: item.labelMaxWidth ? `${item.labelMaxWidth}%` : undefined }}
        >
          {item.label}
        </Typography>
      </Box>

      {/* Row 2: divider */}
      <Box
        sx={{
          gridRow: 2,
          gridColumn: colIndex,
          height: '1px',
          backgroundColor: dividerColor,
          mt: '6px',
          mb: spacingTokens.sm,
          width: '100%',
        }}
      />

      {/* Row 3: value + optional extra */}
      <Box sx={{ gridRow: 3, gridColumn: colIndex }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacingTokens.xs }}>
          <Typography component="div" sx={{ ...VALUE_BASE_SX, color: valueColor }}>{formatDisplayValue(item.value)}</Typography>
          {item.popover}
        </Box>
        {item.extraContent}
      </Box>
    </Box>
  );
};

// ── MetadataGrid ─────────────────────────────────────────────────────────────
// Chunks items into rows based on breakpoint (8 / 4 / 2 / 1 cols).
// Each row is its own 3-shared-row grid → labels, dividers and values align
// perfectly across all columns without any horizontal scroll.
const MetadataGrid = ({ items }: { items: MetaItem[] }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const cols = isLg ? 7 : isMd ? 4 : isSm ? 2 : 1;

  if (items.length === 0) return null;

  const rows: MetaItem[][] = [];
  for (let i = 0; i < items.length; i += cols) {
    rows.push(items.slice(i, i + cols));
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacingTokens.md, width: '100%' }}>
      {rows.map((rowItems, rowIndex) => (
        <Box
          key={rowIndex}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(62px, 1fr))`,
            columnGap: { xs: spacingTokens.md, sm: spacingTokens.lg, md: '36px', lg: '44px' },
          }}
        >
          {rowItems.map((item, i) => (
            <MetadataItem key={`${item.label}-${i}`} item={item} colIndex={i + 1} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

const KnockoutDetails = ({ sepID, sep: sepProp, dataFields: dataFieldsProp }: Props) => {
  const account = useAccount();
  const isAdminUser = env.REACT_APP_LOCAL_DEV_ADMIN === 'true' || (account?.idTokenClaims?.roles?.includes('AuthSuperUser') ?? false);
  const { data: sepFromApi } = useGetSepQuery(sepID ?? (sepProp?.id as number) ?? 0);
  const { data: dataFieldsFromApi } = useGetDataFieldsBySEPIDQuery(sepID ?? (sepProp?.id as number) ?? 0);
  const sep = sepProp || sepFromApi;
  const dataFields = dataFieldsProp || dataFieldsFromApi || [];

  const fieldMap = React.useMemo(() => {
    const map = new Map<string, any>();
    (dataFields || []).forEach((f: any) => map.set(f.name, f));
    return map;
  }, [dataFields]);

  const apmRaw = sep?.apmID || sep?.apmData || null;
  const apmDisplay = apmRaw ? String(apmRaw).padStart(6, '0') : '';

  const { data: apmData, isLoading: apmLoading } = useGetApmQuery(sep?.apmID ?? skipToken);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const apm: APM | null = React.useMemo(() => {
    if (apmData) return apmData;
    if (sep?.apmData) {
      try {
        return typeof sep.apmData === 'string' ? JSON.parse(sep.apmData) : sep.apmData;
      } catch {
        return null;
      }
    }
    return null;
  }, [apmData, sep?.apmData]);

  const topRowItems = TOP_ROW_FIELDS.reduce<MetaItem[]>((acc, col) => {
    let value: string;
    if (col.dbName === '__apmid__') {
      value = apmDisplay;
    } else if (col.dbName === '__requestDate__') {
      value = formatDate(sep?.createdAt);
    } else if (col.dbName === '__validationDate__') {
      value = formatDate(sep?.validationDate);
    } else {
      value = getDataFieldValue(fieldMap.get(col.dbName));
    }
    if (col.always || value !== '') {
      const entry: MetaItem = { label: col.label, value, labelMaxWidth: col.labelMaxWidth };
      if (col.dbName === '__apmid__' && sep?.apmID) {
        entry.popover = isAdminUser && sep?.apmID && (
          <Box
            component="span"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <PopOver component={<APMFields apm={(apm || {}) as APM} />} />
          </Box>
        );
        entry.extraContent = apmLoading ? (
          <CircularProgress size={12} sx={{ mt: spacingTokens.xs }} />
        ) : (
          <MuiLink
            href={`https://ceg.service-now.com/now/nav/ui/classic/params/target/cmdb_ci_business_app.do%3Fsys_id%3D${apmData?.sys_id}%26sysparm_record_target%3Dcmdb_ci_business_app%26sysparm_record_row%3D1%26sysparm_record_rows%3D1%26sysparm_record_list%3DnumberENDSWITH${sep.apmID}%255EORDERBYnumber`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            sx={{ display: 'block', mt: spacingTokens.xs, fontSize: typography.caption.fontSize, color: theme.palette.prime.blue, fontWeight: typographyTokens.fontWeights.regular, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Click here for details
          </MuiLink>
        );
      }
      acc.push(entry);
    }
    return acc;
  }, []);

  const secondaryItems = (dataFields || []).filter(
    (f: any) => !TOP_ROW_DB_NAMES.has(f.name) && getDataFieldValue(f) !== '',
  );

  const participants = sep?.subscribers
    ? sep.subscribers.split(',').map((s: string) => s.trim()).filter(Boolean)
    : [];

  // Combine all items
  const allItems: MetaItem[] = [
    ...topRowItems,
    ...secondaryItems.map((f: any) => ({ label: f.name, value: getDataFieldValue(f), labelMaxWidth: 80 })),
    ...(participants.length > 0 ? [{ label: 'Participants', value: String(participants.length), labelMaxWidth: 80 }] : []),
  ];

  // First 7 items shown by default, rest in collapsible section
  const visibleItems = allItems.slice(0, 7);
  const collapsibleItems = allItems.slice(7);

  return (
    <Box sx={{ mx: { xs: 2, sm: 3, md: 5 }, mt: spacingTokens.sm, mb: spacingTokens.lg }}>
      <Accordion
        elevation={2}
        disableGutters
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <AccordionSummary
          expandIcon={collapsibleItems.length > 0 ? <ExpandMoreIcon /> : null}
          sx={{
            px: '18px',
            py: spacingTokens.md,
            minHeight: 'unset',
            alignItems: 'flex-start',
            cursor: collapsibleItems.length > 0 ? 'pointer' : 'default',
            '& .MuiAccordionSummary-content': { my: 0, mr: 1 },
            '& .MuiAccordionSummary-expandIconWrapper': { mt: spacingTokens.xs },
          }}
        >
          <MetadataGrid items={visibleItems} />
        </AccordionSummary>

        {collapsibleItems.length > 0 && (
          <AccordionDetails sx={{ pl: '18px', pr: '42px', pt: 0, pb: '20px' }}>
            <MetadataGrid items={collapsibleItems} />
          </AccordionDetails>
        )}
      </Accordion>
    </Box>
  );
};

export default KnockoutDetails;
